import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Eye, Layers, List, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  PhaseDocsModalScrollable,
  PhaseDocsModalTabs,
  PhaseDocsModalAccordion,
} from '@/components/PhaseDocsModalVariants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * DocsModalCompare — preview page that lets the owner click through all
 * three candidate "View Docs" modal layouts side-by-side, using real
 * phase data from a chosen phase id.
 *
 * Route: /preview/docs-modal-compare
 * Query: ?phaseId=...   (optional, auto-loads)
 *
 * Inline actions (Add receipt / Request new waiver / Resend email) are
 * STUBBED to toast notifications here — the goal is layout evaluation,
 * not data mutation. When the user picks a winner, the chosen variant
 * gets wired up against the real action handlers in ConstructionPhasesV2.
 */
export default function DocsModalCompare() {
  const params = new URLSearchParams(window.location.search);
  const [phaseId, setPhaseId] = useState(params.get('phaseId') || '');
  const [phase, setPhase] = useState(null);
  const [contractor, setContractor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeModal, setActiveModal] = useState(null); // 'scrollable' | 'tabs' | 'accordion'

  const loadPhase = async (id) => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(`${API}/phases/${id}`);
      setPhase(r.data);
      if (r.data?.contractor_id) {
        try {
          const cr = await axios.get(`${API}/contractors/${r.data.contractor_id}`);
          setContractor(cr.data);
        } catch {
          setContractor({ id: r.data.contractor_id, name: r.data.contractor_name });
        }
      } else {
        setContractor(null);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Phase not found. Check the ID and try again.');
      setPhase(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (params.get('phaseId')) loadPhase(params.get('phaseId'));
  }, []);

  const handleAction = (label) => {
    toast.success(`Action stubbed in preview: ${label}`, {
      description: 'When you pick a layout, this will be wired to the real handler.',
    });
  };

  const optionCards = [
    {
      key: 'scrollable',
      icon: List,
      title: 'Option A — Scrollable',
      desc: 'One long scrollable column. All sections visible in order. Fastest scan.',
      tradeoff: '✓ See everything at once  ✗ Long phases scroll a lot',
    },
    {
      key: 'tabs',
      icon: Square,
      title: 'Option B — Tabs',
      desc: 'Three top tabs: Compliance · Receipts · Waiver History. Switch between focused views.',
      tradeoff: '✓ Less visual clutter, count badges  ✗ Extra click per section',
    },
    {
      key: 'accordion',
      icon: Layers,
      title: 'Option C — Accordion',
      desc: 'Collapsible sections, multiple can be open at once. Compliance opens by default.',
      tradeoff: '✓ User controls density  ✗ Some sections start hidden',
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 p-8" data-testid="docs-modal-compare-page">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900">View Docs — Layout Comparison</h1>
          <p className="text-sm text-slate-600 mt-1">
            Pick a phase to load real data, then click each option to open the same modal styled three ways.
            Inline actions are stubbed (toast on click) so you can focus on visual feel.
          </p>
        </div>

        {/* Phase loader */}
        <div className="bg-white rounded-lg border border-slate-200 p-4 mb-6">
          <Label htmlFor="phase-id-input" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Phase ID
          </Label>
          <div className="flex gap-2 mt-1.5">
            <Input
              id="phase-id-input"
              data-testid="docs-compare-phase-id-input"
              value={phaseId}
              onChange={(e) => setPhaseId(e.target.value)}
              placeholder="Paste a phase ID from any project (e.g. Paint Labor on New Build 38th)"
              className="font-mono text-xs"
            />
            <Button
              onClick={() => loadPhase(phaseId)}
              disabled={!phaseId || loading}
              data-testid="docs-compare-load-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Load phase'}
            </Button>
          </div>
          {error && (
            <div className="text-xs text-rose-600 mt-2" data-testid="docs-compare-error">
              {error}
            </div>
          )}
          {phase && (
            <div className="text-xs text-slate-600 mt-3 bg-slate-50 rounded p-2.5">
              <span className="font-semibold">{phase.name}</span>
              {phase.contractor_name ? <> · {phase.contractor_name}</> : null}
              {phase.lien_waiver_signed ? <span className="ml-2 text-emerald-700">· Lien waiver signed</span> : null}
            </div>
          )}
        </div>

        {/* Three option cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {optionCards.map((opt) => (
            <div
              key={opt.key}
              className="bg-white rounded-lg border border-slate-200 p-5 flex flex-col"
              data-testid={`docs-compare-card-${opt.key}`}
            >
              <opt.icon className="w-7 h-7 text-violet-600 mb-2" />
              <div className="text-base font-bold text-slate-900">{opt.title}</div>
              <p className="text-xs text-slate-600 mt-2 leading-relaxed flex-1">{opt.desc}</p>
              <p className="text-[11px] text-slate-500 mt-3 leading-relaxed border-t border-slate-100 pt-2.5">
                {opt.tradeoff}
              </p>
              <Button
                onClick={() => setActiveModal(opt.key)}
                disabled={!phase}
                className="mt-4 w-full"
                data-testid={`docs-compare-open-${opt.key}`}
              >
                <Eye className="w-4 h-4 mr-1.5" /> Open {opt.title.split('—')[1].trim()}
              </Button>
            </div>
          ))}
        </div>

        {!phase && (
          <div className="text-center text-xs text-slate-500 mt-8">
            Load a phase above to enable the preview buttons.
          </div>
        )}
      </div>

      {/* Render the three modals */}
      {phase && activeModal === 'scrollable' && (
        <PhaseDocsModalScrollable
          isOpen={true}
          onClose={() => setActiveModal(null)}
          phase={phase}
          contractor={contractor}
          onAction={handleAction}
        />
      )}
      {phase && activeModal === 'tabs' && (
        <PhaseDocsModalTabs
          isOpen={true}
          onClose={() => setActiveModal(null)}
          phase={phase}
          contractor={contractor}
          onAction={handleAction}
        />
      )}
      {phase && activeModal === 'accordion' && (
        <PhaseDocsModalAccordion
          isOpen={true}
          onClose={() => setActiveModal(null)}
          phase={phase}
          contractor={contractor}
          onAction={handleAction}
        />
      )}
    </div>
  );
}
